import { api } from "../../../services/api";
import {
  CheckInEntry,
  DimensionKey,
  LifeAreasConfig,
  RingInsight,
  RingNextStep,
  RingSettings,
  RingSnapshot,
} from "./types";

const BASE = "/backend/life-gps";
const QUEUE_KEY = "lifeGpsOfflineQueue";

type Queued = CheckInEntry & { clientTimestamp: string };

const readQueue = (): Queued[] => {
  try {
    return JSON.parse(localStorage.getItem(QUEUE_KEY) || "[]");
  } catch {
    return [];
  }
};
const writeQueue = (q: Queued[]) => {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(q));
  } catch {
    // storage unavailable: nothing to persist
  }
};

// The config only changes with a backend deploy, so keep it for the session.
let configCache: Promise<LifeAreasConfig> | null = null;
export const getConfig = (): Promise<LifeAreasConfig> => {
  if (!configCache) {
    configCache = api
      .get(`${BASE}/config`)
      .then((r) => r.data.data as LifeAreasConfig)
      .catch((err) => {
        configCache = null; // retry next time instead of caching a failure
        throw err;
      });
  }
  return configCache;
};

export const isNetworkError = (err: any) => !err?.response;

export const getSnapshot = async (): Promise<RingSnapshot> =>
  (await api.get(`${BASE}/snapshot`)).data.data;

export const postCheckIn = async (
  updates: CheckInEntry[],
): Promise<{ snapshot: RingSnapshot; insights: RingInsight[] }> => {
  const res = await api.post(`${BASE}/check-in`, { updates });
  return { snapshot: res.data.data, insights: res.data.insights || [] };
};

// Keeps a check-in made while offline; flushQueue() sends it later.
export const queueCheckIn = (entry: CheckInEntry) => {
  const q = readQueue().filter((e) => e.dimensionKey !== entry.dimensionKey);
  q.push({ ...entry, clientTimestamp: new Date().toISOString() });
  writeQueue(q);
};

export const hasQueuedCheckIns = () => readQueue().length > 0;

export const flushQueue = async (): Promise<RingSnapshot | null> => {
  const q = readQueue();
  if (!q.length) return null;
  try {
    const res = await api.post(`${BASE}/sync`, { checkIns: q });
    writeQueue([]); // server decided per entry; skipped ones lost to newer data
    return res.data.data as RingSnapshot;
  } catch (err: any) {
    // A 4xx means the queue itself is bad; drop it rather than retry forever.
    if (!isNetworkError(err) && err.response.status < 500) writeQueue([]);
    return null;
  }
};

export const getHistory = async (key: DimensionKey) =>
  (await api.get(`${BASE}/dimensions/${key}/history`)).data.data
    .history as { score: number; recordedAt: string }[];

export const getNextSteps = async (): Promise<RingNextStep[]> =>
  (await api.get(`${BASE}/next-steps`)).data.data;

export const actOnStep = async (id: string, action: "complete" | "dismiss") =>
  api.patch(`${BASE}/next-steps/${id}`, { action });

export const createStep = async (dimensionKey: DimensionKey, title: string) =>
  api.post(`${BASE}/next-steps`, { dimensionKey, title });

export const getInsights = async (): Promise<RingInsight[]> =>
  (await api.get(`${BASE}/insights`)).data.data;

export const getSettings = async (): Promise<RingSettings> =>
  (await api.get(`${BASE}/settings`)).data.data;

export const putSettings = async (
  patch: Partial<RingSettings>,
): Promise<{ settings: RingSettings; snapshot: RingSnapshot }> => {
  const res = await api.put(`${BASE}/settings`, patch);
  return { settings: res.data.data, snapshot: res.data.snapshot };
};
